import { api } from ".";

// Types for Contract Fee Schedules
export interface IContractFeeSchedule {
  id: string;
  providerId: string;
  payerId: number;
  effectiveDate: string;
  nextUpdateDate?: string;
  status: "Draft" | "Active" | "Inactive" | "Expired" | "Importing";
  proceduresOutdated?: boolean;
  name?: string;
  createdAt: string;
  updatedAt: string;
  mpdrRulesetIds?: string[];
  caseRateRulesetIds?: string[];
  mpdrRulesets?: any[];
  caseRateRulesets?: any[];
  notes?: string;
  createdBy?: {
    id: string;
    email: string;
  };
  provider?: {
    id: string;
    name: string;
    classification: string;
    specialty: string;
  };
  payer?: {
    id: number;
    name: string;
    type: string;
  };
  rateCalculations?: IRateCalculation[];
  carveOutFiles?: ICarveout[];
  attachments?: IAttachment[];
  medicareAdvantagePlans?: IMedicareAdvantageLink[];
  _count?: {
    carveouts: number;
    medicareAdvantageLinks: number;
  };
}

export interface IRateCalculation {
  id?: string;
  baseRateFileId?: string;
  ratePercent: number;
  sortOrder: number;
  baseRateFile?: {
    id: string;
    displayName: string;
    effectiveDate: string;
  };
}

export interface ICarveout {
  id: string;
  fileName: string;
  s3Key: string;
  fileSize: number;
  metadata?: any;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
  };
}

export interface IAttachment {
  id: string;
  fileName: string;
  s3Key: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
  };
}

export interface IMedicareAdvantageLink {
  medicareAdvantagePlanId: number;
  medicareAdvantagePlan?: {
    id: number;
    name: string;
    type: string;
  };
}

export interface IContractFilterParams {
  providerId?: string;
  payerId?: number;
  status?: string;
  search?: string;
}

export interface IPreviewRequest {
  cptCodes?: string[];
  limit?: number;
  carveoutOnly?: boolean;
}

export interface IPreviewResult {
  rates: IRateCalculationResult[];
  total: number;
  totalCarveouts: number;
  hasMore: boolean;
  cacheStats: {
    size: number;
    maxSize: number;
    ttl: number;
  };
}

export interface IRateCalculationResult {
  cptCode: string;
  description: string;
  providerSpecialty: string;
  baseRate: number;
  ratePercent: number;
  calculatedRate: number;
  isCarveout: boolean;
  carveoutRate?: number;
  source: string;
}

const contractFeeSchedulesQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    // Contract Fee Schedule CRUD
    getContractFeeSchedules: builder.query<
      IContractFeeSchedule[],
      IContractFilterParams
    >({
      query: (params) => ({
        url: "contract-fee-schedules",
        params,
      }),
      providesTags: ["ContractFeeSchedules"],
    }),

    getContractFeeSchedule: builder.query<
      { data: IContractFeeSchedule },
      string
    >({
      query: (id) => `contract-fee-schedules/${id}`,
      transformResponse: (response: {
        data: IContractFeeSchedule & { carveouts?: ICarveout[] };
      }) => {
        // Map backend 'carveouts' to frontend 'carveOutFiles'
        if (response.data.carveouts) {
          response.data.carveOutFiles = response.data.carveouts;
          delete (response.data as any).carveouts;
        }
        return response as { data: IContractFeeSchedule };
      },
      providesTags: (result, error, id) => [
        { type: "ContractFeeSchedules", id },
      ],
    }),

    createContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule; id: string },
      Omit<
        IContractFeeSchedule,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "provider"
        | "payer"
        | "_count"
        | "createdBy"
        | "carveOutFiles"
        | "medicareAdvantagePlans"
        | "mpdrRuleset"
        | "caseRateRuleset"
      >
    >({
      query: (body) => ({
        url: "contract-fee-schedules",
        method: "POST",
        body: {
          ...body,
          rateCalculations: body.rateCalculations?.map((calc) => ({
            ...calc,
            ratePercent: Number(calc.ratePercent),
            sortOrder: Number(calc.sortOrder) || 0,
          })),
        },
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    createBulkContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule[]; count: number; message: string },
      {
        providerIds: string[];
        payerIds: number[];
        effectiveDate: string;
        nextUpdateDate?: string;
        rateCalculations: RateCalculation[];
        mpdrRulesetIds?: string[];
        caseRateRulesetIds?: string[];
        medicareAdvantagePayerIds?: number[];
        copyFromContractId?: string;
        carveoutFiles?: File[];
        notes?: string;
      }
    >({
      query: ({ carveoutFiles, ...body }) => {
        // If we have carveout files, send as multipart/form-data with TOP-LEVEL fields
        if (carveoutFiles && carveoutFiles.length > 0) {
          const formData = new FormData();

          // Top-level scalar fields
          formData.append("effectiveDate", body.effectiveDate);
          if (body.nextUpdateDate)
            formData.append("nextUpdateDate", body.nextUpdateDate);

          // Arrays as repeated fields
          (body.providerIds || []).forEach((id) =>
            formData.append("providerIds", id)
          );
          (body.payerIds || []).forEach((id) =>
            formData.append("payerIds", String(id))
          );

          // rateCalculations: send once as JSON string (backend should parse this)
          // Values normalized to numbers to mirror JSON path
          formData.append(
            "rateCalculations",
            JSON.stringify(
              (body.rateCalculations || []).map((calc, index) => ({
                baseRateFileId: calc.baseRateFileId ?? undefined,
                ratePercent: Number(calc.ratePercent),
                sortOrder: Number(calc.sortOrder ?? index) || 0,
              }))
            )
          );

          // Optional arrays
          if (body.mpdrRulesetIds)
            body.mpdrRulesetIds.forEach((id) =>
              formData.append("mpdrRulesetIds", id)
            );
          if (body.caseRateRulesetIds)
            body.caseRateRulesetIds.forEach((id) =>
              formData.append("caseRateRulesetIds", id)
            );
          if (body.medicareAdvantagePayerIds)
            body.medicareAdvantagePayerIds.forEach((id) =>
              formData.append("medicareAdvantagePayerIds", String(id))
            );
          if (body.copyFromContractId)
            formData.append("copyFromContractId", body.copyFromContractId);
          if (body.notes) formData.append("notes", body.notes);

          // Files
          carveoutFiles.forEach((file) => {
            formData.append("carveoutFiles", file);
          });

          return {
            url: "contract-fee-schedules/bulk",
            method: "POST",
            body: formData,
          };
        }

        // Otherwise send as JSON
        return {
          url: "contract-fee-schedules/bulk",
          method: "POST",
          body: {
            ...body,
            rateCalculations: body.rateCalculations?.map((calc) => ({
              ...calc,
              ratePercent: Number(calc.ratePercent),
              sortOrder: Number(calc.sortOrder) || 0,
            })),
          },
        };
      },
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    updateContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule },
      { id: string } & Omit<
        IContractFeeSchedule,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "provider"
        | "payer"
        | "_count"
        | "createdBy"
        | "carveOutFiles"
        | "medicareAdvantagePlans"
        | "status"
        | "mpdrRuleset"
        | "caseRateRuleset"
      >
    >({
      query: ({ id, ...body }) => ({
        url: `contract-fee-schedules/${id}`,
        method: "PUT",
        body: {
          ...body,
          rateCalculations: body.rateCalculations?.map((calc) => ({
            ...calc,
            ratePercent: Number(calc.ratePercent),
            sortOrder: Number(calc.sortOrder) || 0,
          })),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ContractFeeSchedules", id },
        "ContractFeeSchedules",
      ],
    }),

    activateContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule },
      string
    >({
      query: (id) => ({
        url: `contract-fee-schedules/${id}/activate`,
        method: "POST",
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    deleteContractFeeSchedule: builder.mutation<void, string>({
      query: (id) => ({
        url: `contract-fee-schedules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    // Preview and Export
    previewFeeSchedule: builder.mutation<
      IPreviewResult,
      { contractId: string; params: IPreviewRequest }
    >({
      query: ({ contractId, params }) => ({
        url: `contract-fee-schedules/${contractId}/preview`,
        method: "POST",
        body: params,
      }),
    }),

    exportFeeSchedule: builder.mutation<
      Blob,
      { contractId: string; params: IPreviewRequest }
    >({
      query: ({ contractId, params }) => ({
        url: `contract-fee-schedules/${contractId}/export`,
        method: "POST",
        body: params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Carveout Management
    uploadCarveout: builder.mutation<
      ICarveout,
      { contractId: string; file: File }
    >({
      query: ({ contractId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `contract-fee-schedules/${contractId}/carveouts`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    deleteCarveout: builder.mutation<
      void,
      { contractId: string; carveoutId: string }
    >({
      query: ({ contractId, carveoutId }) => ({
        url: `contract-fee-schedules/${contractId}/carveouts/${carveoutId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Attachment Management
    uploadAttachment: builder.mutation<
      IAttachment,
      { contractId: string; file: File }
    >({
      query: ({ contractId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `contract-fee-schedules/${contractId}/attachments`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    deleteAttachment: builder.mutation<
      void,
      { contractId: string; attachmentId: string }
    >({
      query: ({ contractId, attachmentId }) => ({
        url: `contract-fee-schedules/${contractId}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Generate Provider Procedures
    generateProviderProcedures: builder.mutation<
      {
        success?: boolean;
        dryRun?: boolean;
        procedureCount: number;
        procedures?: any[];
        totalRate?: number;
        importedFeeScheduleId?: string;
        message?: string;
        status?: string;
        contractId?: string;
      },
      {
        contractId: string;
        options?: {
          overwriteExisting?: boolean;
          dryRun?: boolean;
        };
      }
    >({
      query: ({ contractId, options }) => ({
        url: `contract-fee-schedules/${contractId}/generate-procedures`,
        method: "POST",
        body: options || {},
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Get outdated contracts
    getOutdatedContracts: builder.query<
      {
        data: IContractFeeSchedule[];
        message: string;
      },
      void
    >({
      query: () => ({
        url: "contract-fee-schedules/outdated",
      }),
      providesTags: ["ContractFeeSchedules"],
    }),

    // Regenerate provider procedures for outdated contract
    regenerateProcedures: builder.mutation<
      {
        data: {
          success: boolean;
          procedureCount: number;
          importedFeeScheduleId: string;
          message: string;
        };
        message: string;
      },
      string
    >({
      query: (contractId) => ({
        url: `contract-fee-schedules/${contractId}/regenerate-procedures`,
        method: "POST",
      }),
      invalidatesTags: (result, error, contractId) => [
        { type: "ContractFeeSchedules", id: contractId },
        "ContractFeeSchedules",
      ],
    }),
  }),
});

// Export query hooks
export const {
  useGetContractFeeSchedulesQuery,
  useGetContractFeeScheduleQuery,
  useCreateContractFeeScheduleMutation,
  useCreateBulkContractFeeScheduleMutation,
  useUpdateContractFeeScheduleMutation,
  useActivateContractFeeScheduleMutation,
  useDeleteContractFeeScheduleMutation,
  usePreviewFeeScheduleMutation,
  useExportFeeScheduleMutation,
  useUploadCarveoutMutation,
  useDeleteCarveoutMutation,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
  useGenerateProviderProceduresMutation,
  useGetOutdatedContractsQuery,
  useRegenerateProceduresMutation,
} = contractFeeSchedulesQueries;

// Re-export the interfaces for use in components
export type ContractFeeSchedule = IContractFeeSchedule;
export type RateCalculation = IRateCalculation;
export type ContractFilterParams = IContractFilterParams;
